import React, { useState, useEffect } from "react";
import { TbX, TbCopy } from "react-icons/tb";
import { api } from "../../lib/api";
import { Button, Field, Alert } from "../../components";
import "./Reviews.css";

// The score groups a library item can belong to: the four QBR quadrants, then the
// categories of the review template library (scripts/seed-qbr-library.js). Fixed rather
// than free text: they are the categories a template is built from, and a typo would
// silently create a group nothing could ever fill to nine.
export const CATEGORIES = [
  "Infrastructure", "Security", "Managed services", "Telecommunications",
  "Roadmap & Budget", "Continuity & Recovery", "Service Performance", "Audit Readiness",
  "Vendors & Licensing", "Technology Strategy",
];

// The chips to offer for one item: the fixed list, with the item's own spelling of any tag
// it already carries, plus any tag outside the list. Without this a tag the list does not
// name (the seeder writes "Managed Services"; the list says "Managed services") was kept
// on save but never shown, so it could not be seen or removed.
const chipsFor = (tags) => {
  const own = (tags || []).filter(Boolean);
  const known = CATEGORIES.map((c) => own.find((t) => t.toLowerCase() === c.toLowerCase()) || c);
  const extra = own.filter((t) => !CATEGORIES.some((c) => c.toLowerCase() === t.toLowerCase()));
  return [...known, ...extra];
};

const INFOGRAPHICS = [
  { value: "none", label: "None" },
  { value: "downtime", label: "Downtime" },
  { value: "cyber_risk", label: "Cyber risk" },
  { value: "custom", label: "Custom image" },
];

// The three pre-written statements, one per rating. This is the whole reason the library
// exists: the same finding reads the same way for every client.
const STATEMENTS = [
  { rating: "red", label: "Immediate attention", cls: "red" },
  { rating: "yellow", label: "Needs improvement", cls: "yellow" },
  { rating: "green", label: "In good shape", cls: "green" },
];

const blank = { summary: "", business_impact: "", recommendation: "" };

// What opens when a row in the item library is clicked.
const LibraryItemDrawer = ({ item, onClose, onSaved }) => {
  const isNew = !!item && !item.id;
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!item) return;
    // `item.id` is absent when creating. The same form serves both, because a new item
    // and an edited one need exactly the same fields — and a separate create dialog
    // would drift out of step with this one the first time a field is added.
    setForm({
      name: item.name || "",
      question: item.question || "",
      categories: item.categories || [],
      default_weight: Number(item.default_weight) || 3,
      default_infographic: item.default_infographic || "none",
      scan_category: item.scan_category || "",
      statements: {
        red: { ...blank, ...(item.statements?.red || {}) },
        yellow: { ...blank, ...(item.statements?.yellow || {}) },
        green: { ...blank, ...(item.statements?.green || {}) },
      },
    });
    setError("");
  }, [item]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  if (!item || !form) return null;

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const setStatement = (rating, field, value) =>
    setForm((f) => ({
      ...f,
      statements: { ...f.statements, [rating]: { ...f.statements[rating], [field]: value } },
    }));

  const toggleCategory = (c) =>
    set({
      categories: form.categories.includes(c)
        ? form.categories.filter((x) => x !== c)
        : [...form.categories, c],
    });

  const save = async () => {
    if (!form.name.trim()) { setError("Give the item a name."); return; }
    if (form.categories.length === 0) { setError("Choose at least one category."); return; }
    setBusy(true); setError("");
    try {
      const payload = {
        name: form.name,
        question: form.question,
        categories: form.categories,
        default_weight: form.default_weight,
        default_infographic: form.default_infographic,
        scan_category: form.scan_category || null,
      };

      // Two calls, because the statements are their own rows. The item first: if the
      // statement write fails, the item itself is still saved rather than both lost.
      const id = isNew
        ? (await api.post("s4/api/review-library", payload))?.id
        : (await api.put(`s4/api/review-library/${item.id}`, payload), item.id);

      if (id) await api.put(`s4/api/review-library/${id}/statements`, { statements: form.statements });
      await onSaved();
      onClose();
    } catch (e) {
      setError(e.message || "Could not save the item.");
    } finally { setBusy(false); }
  };

  const duplicate = async () => {
    setBusy(true); setError("");
    try {
      await api.post("s4/api/review-library", {
        name: `${form.name} (copy)`,
        question: form.question,
        categories: form.categories,
        default_weight: form.default_weight,
        default_infographic: form.default_infographic,
      });
      await onSaved();
      onClose();
    } catch (e) {
      setError(e.message || "Could not duplicate the item.");
    } finally { setBusy(false); }
  };

  return (
    <>
      <div className="v2-drawer-scrim" onClick={onClose} aria-hidden="true" />
      <aside className="v2-drawer v2-drawer--wide" role="dialog" aria-label={`${item.name} detail`}>
        <div className="v2-drawer-head">
          <div style={{ flex: 1, minWidth: 0 }}>
            <h4>{form.name || (isNew ? "New library item" : "Untitled item")}</h4>
            <div className="v2-drawer-sub">{form.question || "No question set"}</div>
          </div>
          {!isNew && (
            <Button onClick={duplicate} disabled={busy}><TbCopy size={14} /> Duplicate item</Button>
          )}
          <Button icon aria-label="Close" onClick={onClose}><TbX size={15} /></Button>
        </div>

        <div className="v2-drawer-body">
          <Alert>{error}</Alert>

          <Field label="Name">
            <input className="v2-input" value={form.name} onChange={(e) => set({ name: e.target.value })} />
          </Field>
          <Field label="Question" hint="What this item is asking, in the consultant's words.">
            <input className="v2-input" value={form.question}
                   onChange={(e) => set({ question: e.target.value })}
                   placeholder="Are restores proven, not just scheduled?" />
          </Field>

          <div>
            <div className="v2-section-title">Categories</div>
            <div className="v2-chiprow">
              {chipsFor(form.categories).map((c) => (
                <button key={c} type="button"
                        className={`v2-chip${form.categories.includes(c) ? " on" : ""}`}
                        aria-pressed={form.categories.includes(c)}
                        onClick={() => toggleCategory(c)}>
                  {c}
                </button>
              ))}
            </div>
            <p className="v2-hint" style={{ marginTop: ".375rem" }}>Choose at least one category.</p>
          </div>

          <div>
            <div className="v2-section-title">Default importance</div>
            <div className="v2-weight" role="radiogroup" aria-label="Default importance">
              {[1, 2, 3, 4, 5].map((n) => (
                <button key={n} type="button" role="radio"
                        aria-checked={form.default_weight === n}
                        aria-label={`${n} of 5`}
                        className={`v2-weight-step${form.default_weight >= n ? " on" : ""}`}
                        onClick={() => set({ default_weight: n })} />
              ))}
              <span className="v2-weight-value">{form.default_weight} of 5</span>
            </div>
          </div>

          <div>
            <div className="v2-section-title">Default infographic</div>
            <div className="v2-chiprow">
              {INFOGRAPHICS.map((g) => (
                <button key={g.value} type="button"
                        className={`v2-chip${form.default_infographic === g.value ? " on" : ""}`}
                        aria-pressed={form.default_infographic === g.value}
                        onClick={() => set({ default_infographic: g.value })}>
                  {g.label}
                </button>
              ))}
            </div>
            <p className="v2-hint" style={{ marginTop: ".375rem" }}>
              Shown automatically for red and yellow findings.
            </p>
          </div>

          <div>
            <div className="v2-section-title">Default status statements</div>
            <div style={{ display: "flex", flexDirection: "column", gap: ".5rem", marginTop: ".375rem" }}>
              {STATEMENTS.map((s) => (
                <div key={s.rating} className={`v2-statement-row v2-statement-row--${s.cls}`}>
                  <div className="v2-statement-title">{s.label}</div>
                  <div className="v2-statement-sub">Summary, business impact, recommendation</div>
                  <textarea className="v2-input" rows={1} placeholder="Summary"
                            value={form.statements[s.rating].summary}
                            onChange={(e) => setStatement(s.rating, "summary", e.target.value)} />
                  <textarea className="v2-input" rows={1} placeholder="Business impact"
                            value={form.statements[s.rating].business_impact}
                            onChange={(e) => setStatement(s.rating, "business_impact", e.target.value)} />
                  <textarea className="v2-input" rows={1} placeholder="Recommendation"
                            value={form.statements[s.rating].recommendation}
                            onChange={(e) => setStatement(s.rating, "recommendation", e.target.value)} />
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="v2-drawer-foot">
          <Button onClick={onClose} disabled={busy}>Cancel</Button>
          <Button variant="primary" style={{ marginLeft: "auto" }} onClick={save} disabled={busy}>
            {busy ? "Saving…" : isNew ? "Create item" : "Save item"}
          </Button>
        </div>
      </aside>
    </>
  );
};

export default LibraryItemDrawer;
